import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Card, CardBody, CardHeader, Col, Input, Nav, NavItem, NavLink, Row, Spinner } from 'reactstrap';
import ApprovedListing from './ApprovedListing';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { propertiesListing } from 'endpoints/properties/propertiesListings.action';
import { useQuery } from '@tanstack/react-query';
import PendingListing from './PendingListing';
import RejectedListing from './RejectedListing';
import SoldListing from './SoldListing';
import RentedListing from './RentedListing';
import ArchivedListing from './ArchivedListing';

interface PropertiesFilters {
    status: string;
    search?: string;
    page: number;
    limit: number;
}

const ManagePropertiesTable = () => {

    const [activeTab, setActiveTab] = useState("accepted");
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [isFirstMount, setIsFirstMount] = useState(true);

    // Create filters object
    const filters: PropertiesFilters = useMemo(() => {
        const baseFilters = {
            status: activeTab === 'accepted' ? 'accepted' : activeTab === 'pending' ? 'pending' : activeTab === 'rejected' ? 'rejected' : activeTab === 'sold' ? 'sold' : activeTab === 'rented' ? 'rented' : 'archived',
            page: currentPage,
            limit: limit
        };

        if (searchTerm && searchTerm.trim() !== '') {
            return {
                ...baseFilters,
                search: searchTerm.trim()
            };
        }

        return baseFilters;
    }, [activeTab, searchTerm, currentPage, limit]);

    // React Query for data fetching
    const {
        data: propertiesData,
        isLoading,
        isError,
        error,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['properties', filters],
        queryFn: () => {
            return propertiesListing(filters);
        },
        enabled: !isFirstMount, // Disable on first mount
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: true,
        refetchInterval: false,
    });

    // Handle first mount manually
    useEffect(() => {
        if (isFirstMount) {
            setIsFirstMount(false);
            // The query will now be enabled and run automatically
        }
    }, [isFirstMount]);

    // Debounced search function
    const debouncedSearch = useRef(
        debounce((value: string) => {
            setSearchTerm(value);
            setCurrentPage(1);
        }, 500)
    ).current;

    // You can directly use debouncedSearch where needed
    const handleSearch = (value: string) => {
        debouncedSearch(value);
    };

    // Handle search input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        handleSearch(value);
    };

    // Clear search function
    const clearSearch = () => {
        setSearchInput("");
        setSearchTerm("");
        setCurrentPage(1);
    };

    // Handle tab change
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setCurrentPage(1);
        clearSearch();
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Handle error display
    useEffect(() => {
        if (isError && error) {
            toast.error('Failed to fetch builders data. Please try again.', {
                position: 'top-right',
                autoClose: 3000,
            });
        }
    }, [isError, error]);

    // Extract data and pagination info
    const properties = propertiesData?.data?.properties || [];
    const totalCount = propertiesData?.data?.total || 0;
    const totalPages = Math.ceil(totalCount / limit);

    return (
        <Card>
            <CardHeader className="py-3">
                <h6 className="my-1">Manage Properties</h6>
            </CardHeader>
            <CardBody>
                <Row className="align-items-center mb-3">
                    <Col>
                        <Nav tabs className="nav nav-tabs nav-tabs-custom nav-success nav-justified mb-0">
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'accepted' })}
                                    onClick={() => handleTabChange('accepted')}
                                    style={{ cursor: 'pointer' }}
                                >Approved
                                    {activeTab === 'accepted' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'pending' })}
                                    onClick={() => handleTabChange('pending')}
                                    style={{ cursor: 'pointer' }}
                                >Pending
                                    {activeTab === 'pending' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'rejected' })}
                                    onClick={() => handleTabChange('rejected')}
                                    style={{ cursor: 'pointer' }}
                                >Rejected
                                    {activeTab === 'rejected' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'sold' })}
                                    onClick={() => handleTabChange('sold')}
                                    style={{ cursor: 'pointer' }}
                                >Sold
                                    {activeTab === 'sold' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'rented' })}
                                    onClick={() => handleTabChange('rented')}
                                    style={{ cursor: 'pointer' }}
                                >Rented
                                    {activeTab === 'rented' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'archived' })}
                                    onClick={() => handleTabChange('archived')}
                                    style={{ cursor: 'pointer' }}
                                >Archived
                                    {activeTab === 'archived' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </Col>
                    <Col md={3} className="text-end">
                        <div className="position-relative">
                            <Input
                                type="text"
                                placeholder="Search Properties..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                className="rounded-2 py-2"
                                style={{
                                    border: '1px solid #e5e7eb',
                                    paddingRight: searchInput ? '40px' : '12px'
                                }}
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                                    onClick={clearSearch}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        padding: '0',
                                        width: '30px',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#6c757d',
                                        zIndex: 5
                                    }}
                                >
                                    <i className="ri-close-line"></i>
                                </button>
                            )}
                        </div>
                    </Col>
                </Row>


                {/* Loading State */}
                {isLoading && (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading...</p>
                    </div>
                )}

                {/* Error State */}
                {isError && (
                    <div className="text-center py-5">
                        <div className="text-danger mb-3">
                            <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h5 className="text-danger">Failed to load data</h5>
                        <p className="text-muted">Please check your connection and try again.</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => refetch()}
                        >
                            <i className="ri-refresh-line me-1"></i>
                            Retry
                        </button>
                    </div>
                )}

                {!isLoading && !isError && (
                    <div>
                        {activeTab === 'accepted' && <ApprovedListing
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                        />}
                        {activeTab === 'pending' && <PendingListing
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                        />}
                        {activeTab === 'rejected' && <RejectedListing
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                        />}
                        {activeTab === 'sold' && <SoldListing
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                        />}
                        {activeTab === 'rented' && <RentedListing
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                        />}
                        {activeTab === 'archived' && <ArchivedListing
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                        />}
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default ManagePropertiesTable;
